<!--
  Research Accelerator surface shell.

  Five mutually exclusive pre-app states guard the modules — no app access,
  access-denied, error, loading and no-entitlement — so a module never renders
  for a user whose roles don't open this app, or against an unresolved
  composition, an unlinked member account, or an unresolved role.
  The active module is switched in by ModuleKey below.
-->
@if (appAccessDenied) {
    <mj-empty-state
        Variant="warning"
        Icon="fa-solid fa-lock"
        Title="You don't have access to this app"
        Message="Your account isn't set up for this app."
        ActionText="Back to Explorer"
        ActionIcon="fa-solid fa-arrow-left"
        (Action)="ReturnToHost()" />
} @else if (accessDenied) {
    <mj-empty-state
        Variant="warning"
        Icon="fa-solid fa-lock"
        Title="This account isn't linked to an AGU member record"
        [Message]="accessDenied"
        ActionText="Back to Explorer"
        ActionIcon="fa-solid fa-arrow-left"
        (Action)="ReturnToHost()" />
} @else if (loadError) {
    <mj-empty-state
        Variant="error"
        Title="Couldn't open this app"
        [Message]="loadError"
        ActionText="Back to Explorer"
        ActionIcon="fa-solid fa-arrow-left"
        (Action)="ReturnToHost()" />
} @else if (!ready) {
    <!-- Opening: 3 skeleton cards (P/proto-states.js:130) while the composition and member resolve. -->
    <agu-skeleton [Count]="3" />
} @else if (modules.length === 0) {
    <!--
      Fail closed. The composition resolved but the user is entitled to none of
      its modules, so there is nothing to show them. Never fall back to "all
      modules" — that would hand every module of every app to whichever user the
      entitlement metadata forgot.
    -->
    <mj-empty-state
        Variant="warning"
        Icon="fa-solid fa-lock"
        Title="You don't have access to this app"
        Message="This app is for AGU members with the matching entitlement. If you believe you should have access, contact your AGU administrator."
        ActionText="Back to Explorer"
        ActionIcon="fa-solid fa-arrow-left"
        (Action)="ReturnToHost()" />
} @else {
    <div class="ra-shell">
        <agu-app-sidebar
            [BrandName]="AppName"
            [Modules]="modules"
            [ActiveModuleKey]="location.ModuleKey"
            [Identity]="Identity"
            [Promo]="Promo"
            [SearchPlaceholder]="HasModule('search') ? 'Search everything' : null"
            [CanReturnToHost]="hostAdapter.CanReturnToHost"
            (ModuleSelected)="SelectModule($event)"
            (ProfileRequested)="OpenModule('profile', 'me')"
            (SearchSubmitted)="OpenSearch($event)"
            (ReturnToHostRequested)="ReturnToHost()"
            (SignOutRequested)="SignOut()">
            <!-- The sidebar Featured placement, after the Science Talent Network module (P/Member_Hub.html:1440-1448). -->
            @if (SidebarAd; as ad) { <agu-ad-slot ad [Placement]="ad" /> }
        </agu-app-sidebar>
        <main class="ra-shell__main">
            <!-- The leaderboard: first child of the main column, above the page band (P/Member_Hub.html:1483-1490); none on phone or with ads off. -->
            @if (Leaderboard; as ad) { <agu-ad-slot [Placement]="ad" /> }
            <!-- The lapsed strip above the module area (P/proto-states.js:134); nothing for active or unknown. -->
            @if (LapsedBanner; as text) { <agu-state-banner Tone="amber" [Text]="text" ActionLabel="Renew membership" (Action)="OnRenew()" /> }
            <!--
              Every module visited so far, mounted once and thereafter HIDDEN
              rather than destroyed. Rendering only the active module destroyed
              the outgoing one, so leaving a finder and coming back lost its
              query, filters, sort, page and results and re-ran its on-mount load
              (finding #19 / suite J-6). Mounting stays lazy — a module appears in
              this list on its first visit, never before — so opening the app
              still costs one module's load. See `WithMountedModule`.

              ngComponentOutlet rather than a @switch over hardcoded selectors:
              the surface must not know which modules exist, or adding one would
              mean editing this template — which is exactly the coupling the
              metadata-driven composition removes.
            -->
            @for (module of mountedModules; track module.Key) {
                <!-- Module enter: main-column rise, 8px, re-armed when this module becomes the active one (P/Member_Hub.html:1823). -->
                <div class="ra-shell__module" [hidden]="module.Key !== location.ModuleKey" [aguRise]="0" [RiseDistance]="8" [RiseKey]="module.Key === location.ModuleKey ? location.ModuleKey : null">
                    <ng-container *ngComponentOutlet="module.RootComponent" />
                </div>
            }
            @if (!ActiveModuleComponent) {
                <!--
                  Reachable only if a location names a module that entitlement
                  filtering removed. The landing logic prevents it; this is the
                  honest floor rather than a blank screen.
                -->
                <mj-empty-state
                    Variant="warning"
                    Icon="fa-solid fa-circle-question"
                    Title="That section isn't available"
                    Message="Pick another section from the menu above." />
            }
        </main>
        <!--
          The phone shell (P/Member_Hub.html:482-1020), under PHONE_MEDIA_QUERY only: the app bar and the bottom tabs replace the
          sidebar (hidden by ra-surface.component.scss), and the avatar opens the drawer. The tabs leave on a detail view
          (P/Member_Hub.html:515) and tuck away while scrolling down (:968-975). On a detail the bar carries the crumb's first
          ancestor — title and back chevron (P/Member_Hub.html:862-868) — since the crumb itself is hidden on a phone (:493).
        -->
        @if (IsPhone) {
            <agu-app-bar
                [Title]="AppBarTitle"
                [BackLabel]="AppBarBackLabel"
                (BackRequested)="AppBarBack()"
                [ShowSearch]="HasModule('search')"
                [Initials]="Identity?.Initials ?? ''"
                [MenuOpen]="drawerOpen"
                (HomeRequested)="GoHome()"
                (SearchRequested)="OpenModule('search', 'results')"
                (AvatarRequested)="OpenDrawer()" />
            @if (!IsDetail) {
                <agu-bottom-tabs
                    [Modules]="phoneTabs"
                    [ActiveModuleKey]="location.ModuleKey"
                    [Hidden]="tabsHidden"
                    (Revealed)="OnTabsRevealed()"
                    (ModuleSelected)="SelectModule($event)" />
            }
            <agu-drawer
                [Open]="drawerOpen"
                [Identity]="Identity"
                [Modules]="modules"
                [ActiveModuleKey]="location.ModuleKey"
                [CanReturnToHost]="hostAdapter.CanReturnToHost"
                (Closed)="CloseDrawer()"
                (ModuleSelected)="SelectModuleFromDrawer($event)"
                (ProfileRequested)="CloseDrawer(); OpenModule('profile', 'me')"
                (ReturnToHostRequested)="ReturnToHost()"
                (SignOutRequested)="SignOut()" />
            <!-- The bookmark toast (P/Member_Hub.html:702-710, 952-966), over the tabs or the detail action bar. -->
            <agu-toast [Detail]="IsDetail" [TabsHidden]="tabsHidden" />
        }
    </div>
}
